// Eine Sammlung (ST-SEA-002 Plan F3, Schnitt 3a; ADR-0065; Bauvorgabe DESIGN
// 3a, Abschnitt 3): Name als h1, die sichtbare Zahl, die Kontakte seitenweise.
//
// DIE ZAHL KOMMT EINMAL VOM SERVER (memberCount der ersten Mitgliederseite).
// Nachladen zaehlt nicht neu, und die Oberflaeche rechnet nichts aus den
// geladenen Zeilen -- sonst gaebe es zwei Zahlen, die auseinanderlaufen.
//
// GENAU EINE PRIMARY: "Exportieren" (Bauvorgabe 3b, Abschnitt 2) -- man
// sammelt, um weiterzuverwenden. Bei einer leeren Sammlung ist er nicht
// gerendert; dann traegt der Leerzustand die Hauptaktion. Umbenennen und
// Beenden stehen im Menue "Weitere Aktionen".

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Card,
  Field,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  ProgressBar,
  makeStyles,
  tokens,
} from '@fluentui/react-components';

import type { PlainMessage } from '../access.ts';
import { selectionSummary, togglePage, toggleSelected, type SelectedParty } from '../classification-batches.ts';
import {
  addMembersInBlocks,
  countLabel,
  endedSentence,
  fetchMembers,
  MEMBER_BLOCK_SIZE,
  PARTLY_REMOVED,
  PARTLY_RESTORED,
  progressText,
  REMOVE_FAILED,
  REMOVE_PROGRESS_LABEL,
  removeUnknownSentence,
  RESTORE_FAILED,
  RESTORE_PROGRESS_LABEL,
  restoreUnknownSentence,
  removedSentence,
  removeMembersInBlocks,
  renamedSentence,
  restoredSentence,
  type CollectionMember,
  type CollectionSummary,
  COLLECTION_UNREACHABLE,
} from '../collections.ts';
import { kindLabel, partyHref } from '../parties.ts';
import { useSeitenueberschrift, useUeberschriftImInhalt } from '../shell/layout.tsx';
import { Brotkrume } from '../shell/brotkrume.tsx';
import { MehrKnopf } from '../shell/mehr-knopf.tsx';
import { AppLink, navigate } from '../shell/router.tsx';
import { Skelett, SKELETT_SAMMLUNG } from '../shell/skelett.tsx';
import { StatusPille } from '../shell/status-pille.tsx';
import { Tabelle, type TabellenSpalte } from '../shell/tabelle.tsx';
import { TypSymbol } from '../shell/typ-symbol.tsx';
import { Zustand } from '../shell/zustand.tsx';
import { EXPORT_BUTTON_ID, ExportKnopf, ExportMeldung, type ExportMeldungArt, type ExportOmitChannel } from './kontakte-export.tsx';
import { BeendenDialog, nachBeendenAnsagen, UmbenennenDialog } from './sammlung-pflege.tsx';
import { Meldung } from './zur-sammlung.tsx';

export interface SammlungScreenProps {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  readonly collectionId: string;
}

/**
 * Nach "Neue Sammlung" geht der Fokus auf die h1 der neuen Sammlung. Ein
 * Merker statt eines Adressteils: ein kopierter Link soll den Fokus nicht
 * verschieben.
 */
let titelFokussieren = false;
export function titelNachAnlageFokussieren(): void {
  titelFokussieren = true;
}


const useStyles = makeStyles({
  // Schmal stehen die Knoepfe unter dem Namen; der Name wird nie gekuerzt.
  kopf: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem 1rem' },
  // Der Name nimmt den Platz neben den Knoepfen, nicht die ganze Zeile (DESIGN, 1024 px mit 62 Zeichen).
  titelPlatz: { flex: '1 1 20rem', minWidth: 0 },
  knoepfe: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  // Die Typografie von Title3 wie jede Seiten-h1 (24 px); sonst waere die h2 des
  // Leerzustands groesser. Ein eigenes h1, weil Title3 keinen
  // Fokus-Ref nimmt und der Fokus nach dem Anlegen hierher geht.
  titel: {
    margin: '0.25rem 0 0',
    fontSize: tokens.fontSizeBase600,
    lineHeight: tokens.lineHeightBase600,
    fontWeight: tokens.fontWeightSemibold,
    overflowWrap: 'anywhere',
  },
  // Zahl und Satz in einer Metazeile unter dem Namen (v4 Kontaktkopf), schmal untereinander.
  // Ohne Trennpunkt: bricht der Satz schmal um, haengt kein Zeichen am Zeilenende.
  meta: { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '0 0.75rem', marginTop: '0.25rem', color: tokens.colorNeutralForeground2 },
  zahl: { fontVariantNumeric: 'tabular-nums', color: tokens.colorNeutralForeground1, fontWeight: tokens.fontWeightSemibold },
  // Eine Karte wie die Uebersicht (v4 .panel); die Zeilen laufen bis an den Rand.
  karte: {
    marginTop: '1rem',
    padding: 0,
    gap: 0,
    boxShadow: tokens.shadow4,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  kartenInhalt: { padding: '1rem' },
  // Ergebniszeile ueber der Tabelle (v4): der Satz zur Auswahl; die Flaeche traegt der Tabellenkopf.
  auswahl: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.25rem 1rem', padding: '0.625rem 1rem' },
  // Wie der Name in der Kontaktliste: Symbol und Name, unterstrichen (WCAG 1.4.1).
  name: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
    textDecorationLine: 'underline',
    textUnderlineOffset: '2px',
    overflowWrap: 'anywhere',
    '& > svg': { flexShrink: 0 },
  },
  aktionen: { display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.75rem' },
  ansage: { display: 'block', marginTop: '0.5rem', overflowWrap: 'anywhere' },
  verborgen: { position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' },
});

/**
 * Die Zahl im Tabellenfuss (v4 `.listfoot`). "von M" nur, solange es eine
 * weitere Seite gibt: kam die letzte leer (inzwischen entfernt oder
 * unsichtbar), ist M ueberholt, und "2 von 3" ohne Knopf waere eine Sackgasse.
 */
function shownLabel(angezeigt: number, gesamt: number, weitere: boolean): string {
  const zahl = (n: number) => n.toLocaleString('de-DE');
  return weitere && angezeigt < gesamt ? `${zahl(angezeigt)} von ${zahl(gesamt)} angezeigt` : `${zahl(angezeigt)} angezeigt`;
}

const MEHR_ID = 'sammlung-weitere-aktionen';
const RUECKGAENGIG_ID = 'sammlung-rueckgaengig';
const ALLE_NAME = 'Alle auf dieser Seite auswählen';
/** Das Kopfkaestchen der Tabelle; Tabelle vergibt keine id, es heisst eindeutig. */
const alleKaestchen = (): HTMLElement | null =>
  document.querySelector<HTMLElement>(`table[aria-label="Kontakte der Sammlung"] thead input[aria-label="${ALLE_NAME}"]`);
const WEITERE_LADEN_ID = 'sammlung-weitere-laden';
/** Dasselbe Zeichen wie "Weitere Aktionen" im Kontaktkopf. */
const MEHR = (
  <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="5.5" cy="12" r="1.2" />
    <circle cx="12" cy="12" r="1.2" />
    <circle cx="18.5" cy="12" r="1.2" />
  </svg>
);
const GONE_SENTENCE = 'Diese Sammlung gibt es nicht mehr.';

type Geladen = {
  readonly sammlung: CollectionSummary & { readonly memberCount: number };
  readonly kontakte: readonly CollectionMember[];
  readonly cursor: string | undefined;
};

export function SammlungScreen({ apiBaseUrl, accessToken, onReauthenticate, collectionId }: SammlungScreenProps): ReactElement {
  useUeberschriftImInhalt();
  const own = useStyles();
  const [status, setStatus] = useState<'laedt' | 'geladen' | 'weg' | 'abgelehnt'>('laedt');
  const [stand, setStand] = useState<Geladen | undefined>(undefined);
  const [ladefehler, setLadefehler] = useState<{ readonly message: PlainMessage; readonly weitere: boolean } | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [nachErneut, setNachErneut] = useState(false);
  const [dialog, setDialog] = useState<'export' | 'umbenennen' | 'beenden' | undefined>(undefined);
  // "Nicht da" erst nach dem Aushaengen des Dialogs (siehe zurUebersicht).
  const [wegNachDialog, setWegNachDialog] = useState(false);
  const [ansage, setAnsage] = useState('');
  const [exportMeldung, setExportMeldung] = useState<{ readonly art: ExportMeldungArt; readonly weggelassen?: ExportOmitChannel | undefined; readonly nr: number } | undefined>(undefined);
  const [auswahl, setAuswahl] = useState<readonly SelectedParty[]>([]);
  /** Die eben entfernten Kennungen fuer "Rueckgaengig"; ohne Zeitlimit, bis zur naechsten Aenderung. */
  const [rueckweg, setRueckweg] = useState<readonly string[] | undefined>(undefined);
  const [aenderung, setAenderung] = useState<{ readonly art: 'entfernen' | 'zurueck'; readonly fortschritt: number | undefined } | undefined>(undefined);
  // Wohin der Fokus nach dem naechsten Commit geht. Ein setTimeout nach einem
  // await liefe vor dem Commit von React und faende den Knopf noch nicht.
  // `ab:<n>`: nach "Weitere laden" ohne weitere Seite verschwindet der Knopf;
  // der Fokus geht an die erste neue Zeile statt an BODY.
  const fokusZiel = useRef<'rueckgaengig' | 'titel' | 'alle' | `ab:${number}` | undefined>(undefined);
  useEffect(() => {
    const ziel = fokusZiel.current;
    if (ziel === 'alle') {
      fokusZiel.current = undefined;
      alleKaestchen()?.focus();
    } else if (ziel?.startsWith('ab:') === true) {
      // Erst wenn die neuen Zeilen stehen: bleibt der Knopf, behaelt er den Fokus.
      const zeilen = document.querySelectorAll<HTMLInputElement>('table[aria-label="Kontakte der Sammlung"] tbody input[type="checkbox"]');
      const zeile = zeilen[Number(ziel.slice(3))];
      const knopf = document.getElementById(WEITERE_LADEN_ID);
      if (zeile !== undefined) {
        fokusZiel.current = undefined;
        if (knopf === null) zeile.focus();
      } else if (!busy && knopf === null) {
        // Die letzte Seite kam leer (inzwischen entfernt oder unsichtbar): keine neue Zeile.
        fokusZiel.current = undefined;
        (alleKaestchen() ?? titel.current)?.focus();
      }
    } else if (fokusZiel.current === 'rueckgaengig') {
      const knopf = document.getElementById(RUECKGAENGIG_ID);
      if (knopf !== null) {
        fokusZiel.current = undefined;
        knopf.focus();
      }
    } else if (fokusZiel.current === 'titel' && titel.current !== null) {
      fokusZiel.current = undefined;
      titel.current.focus();
    }
  });
  const [aenderungsFehler, setAenderungsFehler] = useState<PlainMessage | undefined>(undefined);
  const titel = useRef<HTMLHeadingElement | null>(null);
  // Der Export meldete "gibt es nicht mehr": nach dem Schliessen des Dialogs neu laden.
  const exportQuelleWeg = useRef(false);
  const laufRef = useRef(0);
  // Wie viele Zeilen die Ansicht zeigt: das stille Neuladen holt mindestens so viele wieder.
  const geladeneZeilen = useRef(0);
  geladeneZeilen.current = stand?.kontakte.length ?? 0;
  useSeitenueberschrift(status === 'geladen' ? stand?.sammlung.collectionName : undefined);

  // `still`: neu laden, ohne die Ansicht zu ersetzen -- weder durch das Skelett
  // noch durch den Fehlerzustand; ein offener Dialog bleibt eingehaengt und
  // sagt selbst, ob es geklappt hat (Rueckgabe).
  const laden = useCallback(
    async (fortsetzen?: string, still = false): Promise<boolean> => {
      const meinLauf = ++laufRef.current;
      const vorher = still && fortsetzen === undefined ? geladeneZeilen.current : 0;
      if (fortsetzen === undefined && !still) setStatus('laedt');
      if (!still) setLadefehler(undefined);
      setBusy(true);
      try {
        const token = await accessToken();
        if (token === undefined) {
          onReauthenticate();
          return false;
        }
        const result = await fetchMembers({ baseUrl: apiBaseUrl, accessToken: token, collectionId, cursor: fortsetzen });
        if (laufRef.current !== meinLauf) return false;
        if (result.outcome === 'gone') {
          // Ein stehengebliebener Merker verschoebe sonst spaeter den Fokus einer anderen Sammlung.
          titelFokussieren = false;
          // Still neu geladen heisst: ein Dialog kann offen sein. Erst er, dann
          // die Ansicht (wie zurUebersicht).
          if (still) {
            setDialog(undefined);
            setWegNachDialog(true);
          } else {
            setStatus('weg');
          }
          return false;
        }
        if (result.outcome === 'rejected') {
          titelFokussieren = false;
          if (still) return false;
          setLadefehler({ message: result.message, weitere: fortsetzen !== undefined });
          if (fortsetzen === undefined) setStatus('abgelehnt');
          return false;
        }
        // Still neu geladen: so viele Seiten nacheinander, bis die Ansicht wieder
        // mindestens so viele Zeilen zeigt wie vorher -- wer "Weitere laden"
        // gedrueckt hatte, verliert sie nicht. Keine Obergrenze, die Seiten sind
        // technisch. Scheitert eine Folgeseite, bleibt, was schon da ist, und
        // "Weitere laden" holt den Rest.
        // Ein wiederholter Cursor waere eine Endlosschleife, keine weitere Seite.
        let kontakte = result.members;
        let naechste = result.nextCursor;
        const gesehen = new Set<string>();
        while (naechste !== undefined && kontakte.length < vorher && !gesehen.has(naechste)) {
          gesehen.add(naechste);
          const weiter = await fetchMembers({ baseUrl: apiBaseUrl, accessToken: token, collectionId, cursor: naechste });
          if (laufRef.current !== meinLauf) return false;
          if (weiter.outcome !== 'loaded') break;
          kontakte = [...kontakte, ...weiter.members];
          naechste = weiter.nextCursor;
        }
        // Ein Kreis-Cursor ist keine weitere Seite: „Weitere laden“ bekommt ihn nicht.
        if (naechste !== undefined && gesehen.has(naechste)) naechste = undefined;
        // Beim Nachladen bleibt die Zahl der ersten Seite stehen.
        setStand((alt) =>
          fortsetzen === undefined || alt === undefined
            ? { sammlung: result.collection, kontakte, cursor: naechste }
            : { sammlung: alt.sammlung, kontakte: [...alt.kontakte, ...result.members], cursor: result.nextCursor },
        );
        setStatus('geladen');
        return true;
      } catch {
        titelFokussieren = false;
        if (laufRef.current !== meinLauf || still) return false;
        setLadefehler({ message: COLLECTION_UNREACHABLE, weitere: fortsetzen !== undefined });
        if (fortsetzen === undefined) setStatus('abgelehnt');
        return false;
      } finally {
        if (laufRef.current === meinLauf) setBusy(false);
      }
    },
    [accessToken, apiBaseUrl, collectionId, onReauthenticate],
  );

  useEffect(() => {
    void laden();
  }, [laden]);

  // Erst nach dem Aushaengen des Dialogs zur Uebersicht: im selben Lauf haengt die
  // Route den alertdialog ab, waehrend Tabster die Wurzel noch modal fuehrt, und
  // seine Platzhalter bleiben tabbar unter aria-hidden (axe aria-hidden-focus, A83).
  const [zurUebersicht, setZurUebersicht] = useState(false);
  // Dasselbe fuer "nicht da" nach einem stillen Neuladen aus dem Dialog.
  useEffect(() => {
    if (!wegNachDialog || dialog !== undefined) return;
    setWegNachDialog(false);
    setStatus('weg');
  }, [dialog, wegNachDialog]);
  useEffect(() => {
    if (!zurUebersicht || dialog !== undefined) return;
    setZurUebersicht(false);
    navigate('/sammlungen');
  }, [dialog, zurUebersicht]);

  useEffect(() => {
    if (status === 'geladen' && titelFokussieren) {
      titelFokussieren = false;
      titel.current?.focus();
    }
  }, [status]);

  if (status === 'laedt') {
    return <Skelett ladesatz="Sammlung wird geladen" zeilen={SKELETT_SAMMLUNG} />;
  }
  if (status === 'weg') {
    return (
      <Zustand
        art="nicht-da"
        titel="Diese Sammlung gibt es nicht mehr"
        saetze={['Sie wurde beendet, oder die Adresse stimmt nicht.']}
        haupt={{ label: 'Zu den Sammlungen', href: '/sammlungen' }}
      />
    );
  }
  if (status === 'abgelehnt' || stand === undefined) {
    const message = ladefehler?.message ?? COLLECTION_UNREACHABLE;
    return (
      <Zustand
        art="fehler"
        titel={message.headline}
        saetze={[message.explanation, message.nextStep]}
        vorgangsnummer={message.correlationId}
        haupt={{
          label: 'Erneut versuchen',
          onClick: () => {
            setNachErneut(true);
            void laden();
          },
        }}
        fokus={nachErneut}
      />
    );
  }

  const { sammlung, kontakte, cursor } = stand;
  const zugang = { apiBaseUrl, accessToken, onReauthenticate };
  const zuMehr = (): void => {
    setTimeout(() => document.getElementById(MEHR_ID)?.focus(), 0);
  };
  const weg = (): void => {
    setDialog(undefined);
    nachBeendenAnsagen(GONE_SENTENCE);
    setZurUebersicht(true);
  };
  const leer = sammlung.memberCount === 0 && kontakte.length === 0;
  const gewaehlt = new Set(auswahl.map((party) => party.partyId));
  const seite: readonly SelectedParty[] = kontakte.map((kontakt) => ({ partyId: kontakt.partyId, displayName: kontakt.displayName }));
  const seiteGanz = seite.length > 0 && seite.every((party) => gewaehlt.has(party.partyId));

  /**
   * Die naechste Aktion auf der Seite leert den Ergebnissatz samt "Rückgängig"
   * (DESIGN): stehen geblieben laese man ihn als Ergebnis der neuen Auswahl.
   * Ohne Zeitlimit und ohne Ansage beim Leeren; der Fokus bleibt am Element der
   * Aktion.
   */
  const naechsteAktion = (): void => {
    setAnsage('');
    setRueckweg(undefined);
  };

  /** Ein Satz zu etwas anderem: "Rückgängig" gehoert nur zum Satz des Entfernens. */
  const melden = (satz: string): void => {
    setRueckweg(undefined);
    setAenderungsFehler(undefined);
    setAnsage(satz);
  };

  /** Die neue sichtbare Zahl aus der letzten Antwort; die Zeilen kommen danach still neu. */
  const zahlUebernehmen = (memberCount: number): void => {
    setStand((alt) => (alt === undefined ? alt : { ...alt, sammlung: { ...alt.sammlung, memberCount } }));
  };

  const entfernen = async (): Promise<void> => {
    if (aenderung !== undefined) return;
    const kennungen = auswahl.map((party) => party.partyId);
    const name = sammlung.collectionName;
    setAenderung({ art: 'entfernen', fortschritt: kennungen.length > MEMBER_BLOCK_SIZE ? 0 : undefined });
    setAenderungsFehler(undefined);
    naechsteAktion();
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const result = await removeMembersInBlocks({
        baseUrl: apiBaseUrl,
        accessToken: token,
        collectionId,
        partyIds: kennungen,
        ...(kennungen.length > MEMBER_BLOCK_SIZE ? { fortschritt: (gesendet: number) => setAenderung({ art: 'entfernen', fortschritt: gesendet }) } : {}),
      });
      if (result.outcome === 'fertig') {
        setAuswahl([]);
        zahlUebernehmen(result.collection.memberCount);
        setRueckweg(kennungen);
        setAnsage(removedSentence(name, kennungen.length, result.collection.memberCount));
        // Der Knopf verschwindet mit der Auswahl; weiter geht es mit "Rückgängig".
        fokusZiel.current = 'rueckgaengig';
        void laden(undefined, true);
        return;
      }
      if (result.outcome === 'gescheitert' && result.weg === true) {
        weg();
        return;
      }
      // Die Auswahl bleibt (NFR-UX-005); was schon entfernt ist, zeigen die neu geladenen Zeilen.
      setAenderungsFehler(result.outcome === 'gescheitert' && result.teilweise ? PARTLY_REMOVED : REMOVE_FAILED);
      if (result.outcome === 'gescheitert' && result.teilweise) void laden(undefined, true);
    } catch {
      // Netzabbruch: der Server hat vielleicht schon entfernt. Ausgang unklar,
      // still neu laden, Auswahl behalten und "Rückgängig" anbieten -- beide
      // Wege sind idempotent.
      const neuGeladen = await laden(undefined, true);
      setRueckweg(kennungen);
      setAnsage(removeUnknownSentence(kennungen.length, neuGeladen));
      fokusZiel.current = 'rueckgaengig';
    } finally {
      setAenderung(undefined);
    }
  };

  const rueckgaengig = async (): Promise<void> => {
    if (rueckweg === undefined || aenderung !== undefined) return;
    const kennungen = rueckweg;
    const name = sammlung.collectionName;
    setAenderung({ art: 'zurueck', fortschritt: kennungen.length > MEMBER_BLOCK_SIZE ? 0 : undefined });
    setAenderungsFehler(undefined);
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const result = await addMembersInBlocks({
        baseUrl: apiBaseUrl,
        accessToken: token,
        collectionId,
        partyIds: kennungen,
        ...(kennungen.length > MEMBER_BLOCK_SIZE ? { fortschritt: (gesendet: number) => setAenderung({ art: 'zurueck', fortschritt: gesendet }) } : {}),
      });
      if (result.outcome === 'fertig') {
        setRueckweg(undefined);
        zahlUebernehmen(result.collection.memberCount);
        setAnsage(restoredSentence(name, result.collection.memberCount));
        // "Rückgängig" verschwindet; der Fokus geht an den Namen der Sammlung.
        fokusZiel.current = 'titel';
        void laden(undefined, true);
        return;
      }
      if (result.outcome === 'gescheitert' && result.weg === true) {
        weg();
        return;
      }
      // Eigene Saetze wie beim Entfernen: sie sagen, was NICHT geschah, und "Rückgängig" bleibt der Weg.
      setAenderungsFehler(result.outcome === 'gescheitert' && result.teilweise ? PARTLY_RESTORED : RESTORE_FAILED);
      if (result.outcome === 'gescheitert' && result.teilweise) void laden(undefined, true);
    } catch {
      const neuGeladen = await laden(undefined, true);
      setAnsage(restoreUnknownSentence(name, kennungen.length, neuGeladen));
      fokusZiel.current = 'rueckgaengig';
    } finally {
      setAenderung(undefined);
    }
  };

  // Wie die Kontaktliste (shell/tabelle.tsx, #501): Name mit Typ-Symbol als Zeilenueberschrift, die Art
  // fuer Hilfsmittel als Text daneben, keine eigene Spalte; Status immer als Pille.
  const spalten: readonly TabellenSpalte<CollectionMember>[] = [
    {
      id: 'name',
      kopf: 'Name',
      zelle: (kontakt) => (
        <>
          <AppLink to={partyHref(kontakt.partyId)} className={own.name}>
            <TypSymbol organisation={kontakt.kind === 'organization'} groesse={20} />
            {kontakt.displayName}
          </AppLink>
          <span className={own.verborgen}>, {kindLabel(kontakt.kind)}</span>
        </>
      ),
    },
    { id: 'status', kopf: 'Status', zelle: (kontakt) => <StatusPille status={kontakt.lifecycleStatus} /> },
  ];

  const gesamt = aenderung?.fortschritt !== undefined ? (rueckweg ?? auswahl.map((party) => party.partyId)).length : 0;
  return (
    <div>
      <Brotkrume schritte={[{ label: 'Sammlungen', to: '/sammlungen' }]} hier={sammlung.collectionName} />
      <div className={own.kopf}>
        <div className={own.titelPlatz}>
          <h1 ref={titel} tabIndex={-1} className={own.titel}>
            {sammlung.collectionName}
          </h1>
        </div>
        <div className={own.knoepfe}>
          {leer ? undefined : (
            <ExportKnopf
              {...zugang}
              quelle={{ art: 'sammlung', collectionId: sammlung.collectionId, name: sammlung.collectionName, anzahl: sammlung.memberCount }}
              markiert={auswahl.map((party) => party.partyId)}
              appearance="primary"
              offen={dialog === 'export'}
              onOffen={(offen) => {
                setDialog(offen ? 'export' : undefined);
                if (!offen && exportQuelleWeg.current) {
                  exportQuelleWeg.current = false;
                  void laden();
                }
              }}
              onQuelleWeg={() => {
                exportQuelleWeg.current = true;
              }}
              onUnklar={() => setExportMeldung((alt) => ({ art: 'unklar', nr: (alt?.nr ?? 0) + 1 }))}
              onAngelegt={(_item, weggelassen) => {
                setExportMeldung((alt) => ({ art: 'angelegt', weggelassen, nr: (alt?.nr ?? 0) + 1 }));
                setTimeout(() => document.getElementById(EXPORT_BUTTON_ID)?.focus(), 0);
              }}
            />
          )}
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <MehrKnopf id={MEHR_ID} appearance="subtle" icon={MEHR} />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem
                  onClick={() => {
                    naechsteAktion();
                    setDialog('umbenennen');
                  }}
                >
                  Umbenennen …
                </MenuItem>
                <MenuDivider />
                <MenuItem onClick={() => setDialog('beenden')}>Sammlung beenden …</MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </div>
      <div className={own.meta}>
        <Body1 className={own.zahl}>{sammlung.memberCount === 0 ? '0 Kontakte' : countLabel(sammlung.memberCount)}</Body1>
        <Body1>Nur die Kontakte, die du hinzugefügt hast. Spätere Suchen ändern sie nicht.</Body1>
      </div>
      {/* Immer im DOM, leer ausserhalb des Flusses: so greift die Ansage beim ersten Satz. */}
      <div role="status" aria-live="polite" aria-label="Status: Sammlung" className={ansage.length === 0 ? own.verborgen : own.ansage}>
        {ansage}
        {rueckweg !== undefined ? (
          <>
            {' '}
            <Button id={RUECKGAENGIG_ID} appearance="secondary" size="small" disabledFocusable={aenderung !== undefined} onClick={() => void rueckgaengig()}>
              Rückgängig
            </Button>
          </>
        ) : undefined}
      </div>
      {aenderungsFehler !== undefined ? <Meldung message={aenderungsFehler} alert /> : undefined}
      {exportMeldung === undefined ? undefined : <ExportMeldung key={exportMeldung.nr} art={exportMeldung.art} weggelassen={exportMeldung.weggelassen} onSchliessen={() => setExportMeldung(undefined)} />}
      {aenderung?.fortschritt !== undefined ? (
        <Field label={aenderung.art === 'zurueck' ? RESTORE_PROGRESS_LABEL : REMOVE_PROGRESS_LABEL} hint={progressText(aenderung.fortschritt, gesamt)}>
          <ProgressBar value={aenderung.fortschritt} max={gesamt} aria-valuetext={progressText(aenderung.fortschritt, gesamt)} />
        </Field>
      ) : undefined}

      <Card className={own.karte}>
        {leer ? (
          <div className={own.kartenInhalt}>
            <Zustand
              art="leer"
              titel="Diese Sammlung ist noch leer"
              saetze={['Such Kontakte und füg sie mit „Zur Sammlung …“ hinzu. Du kannst einzelne Kontakte auswählen oder alle Treffer einer Suche übernehmen.']}
              haupt={{ label: 'Kontakte suchen', href: '/kontakte' }}
            />
          </div>
        ) : (
          <>
            {/* Der Satz zur Auswahl als Kopfzeile der Karte (v4 Ergebniszeile); das Kaestchen
                "Alle auf dieser Seite auswählen" steht im Tabellenkopf. */}
            <div className={own.auswahl}>
              <Caption1>{selectionSummary(auswahl.length)}</Caption1>
            </div>
            <Tabelle
              name="Kontakte der Sammlung"
              zeilen={kontakte}
              schluessel={(kontakt) => kontakt.partyId}
              spalten={spalten}
              auswahl={{
                gewaehlt: (kontakt) => gewaehlt.has(kontakt.partyId),
                // Waehrend Entfernen oder Rueckgaengig laufen, aendert sich die Auswahl nicht.
                umschalten: (kontakt) => {
                  if (aenderung !== undefined) return;
                  naechsteAktion();
                  setAuswahl(toggleSelected(auswahl, { partyId: kontakt.partyId, displayName: kontakt.displayName }));
                },
                name: (kontakt) => `${kontakt.displayName} auswählen`,
                kopf: {
                  name: ALLE_NAME,
                  stand: seiteGanz ? true : seite.some((party) => gewaehlt.has(party.partyId)) ? 'mixed' : false,
                  umschalten: () => {
                    if (aenderung !== undefined) return;
                    naechsteAktion();
                    setAuswahl(togglePage(auswahl, seite));
                  },
                },
                gesperrt: aenderung !== undefined,
              }}
              fuss={
                <>
                  <span>{shownLabel(kontakte.length, sammlung.memberCount, cursor !== undefined)}</span>
                  {cursor !== undefined ? (
                    <Button
                      id={WEITERE_LADEN_ID}
                      appearance="secondary"
                      // Auch waehrend des Entfernens gesperrt, wie die Auswahl: eine neue Seite
                      // mischte sonst Zeilen in eine Liste, die gerade kleiner wird.
                      disabledFocusable={busy || aenderung !== undefined}
                      onClick={() => {
                        if (busy || aenderung !== undefined) return;
                        naechsteAktion();
                        fokusZiel.current = `ab:${kontakte.length}`;
                        void laden(cursor).then((geladen) => {
                          if (!geladen && fokusZiel.current?.startsWith('ab:') === true) fokusZiel.current = undefined;
                        });
                      }}
                    >
                      {ladefehler?.weitere === true ? 'Erneut laden' : 'Weitere laden'}
                    </Button>
                  ) : undefined}
                </>
              }
            />
          </>
        )}
      </Card>

      {ladefehler?.weitere === true ? <Meldung message={ladefehler.message} lesefehler /> : undefined}

      {/* Secondary, nur mit Auswahl; kein Bestaetigungsdialog -- der Rueckweg ist "Rückgängig". */}
      {auswahl.length > 0 ? (
        <div className={own.aktionen}>
          <Button appearance="secondary" disabledFocusable={aenderung !== undefined} onClick={() => void entfernen()}>
            {aenderung !== undefined ? 'Wird entfernt …' : 'Aus Sammlung entfernen'}
          </Button>
          <Button
            appearance="secondary"
            disabledFocusable={aenderung !== undefined}
            onClick={() => {
              naechsteAktion();
              setAuswahl([]);
              // Der Knopf verschwindet mit der Auswahl; der Fokus geht an "Alle auf dieser Seite auswählen".
              fokusZiel.current = 'alle';
            }}
          >
            Auswahl leeren
          </Button>
        </div>
      ) : undefined}

      {dialog === 'umbenennen' ? (
        <UmbenennenDialog
          {...zugang}
          sammlung={sammlung}
          onSchliessen={() => {
            setDialog(undefined);
            zuMehr();
          }}
          onUmbenannt={(neu) => {
            setDialog(undefined);
            // Die Zahl bleibt die der Ansicht; die Antwort traegt den neuen Stand.
            setStand((alt) => (alt === undefined ? alt : { ...alt, sammlung: { ...alt.sammlung, collectionName: neu.collectionName, lockVersion: neu.lockVersion } }));
            melden(renamedSentence(neu.collectionName));
            setTimeout(() => titel.current?.focus(), 0);
          }}
          onWeg={weg}
          onNeuLaden={() => laden(undefined, true)}
        />
      ) : undefined}
      {dialog === 'beenden' ? (
        <BeendenDialog
          {...zugang}
          sammlung={sammlung}
          onSchliessen={() => {
            setDialog(undefined);
            zuMehr();
          }}
          onBeendet={() => {
            setDialog(undefined);
            nachBeendenAnsagen(endedSentence(sammlung.collectionName));
            setZurUebersicht(true);
          }}
          onWeg={weg}
          onNeuLaden={() => laden(undefined, true)}
        />
      ) : undefined}
    </div>
  );
}
